import React, { useState, useContext, useEffect } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, TextField,
  DialogActions, Select, MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails, Input

} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  addPMTilesCircleLayer,
  addPMTilesCircleGroupLayer,
  addPMTilesRasterLayer,
  defaultSgeLayerOptions,
  defaultFactorHexagonLayerOptions
} from "../../common/layer/addPMTilelayers"

import { addGeoTiffLayer } from "../../common/layer/addGeoTiffLayer";
import { loadCatalog, loadCatalogAsIs } from "../Catalog/LoadCatalogButton";
import LayersFromCatalog from './LayersFromCatalog';

import { MapContext } from '../MapContext';
import { LayerContext } from '../LayerContext';
import { useCatalog } from '../CatalogContext';
import './AddAssetButton.css';


export const ASSET_TYPES = {
  BACKGROUND: {
    prefix: 'bg',
    decription: "histology or other backgrounds"
  },
  FICTURE: {
    prefix: 'fic',
    decription: "FICTURE LDA"
  },
  HEXAGON: {
    prefix: 'hexa',
    decription: "hexagonal LDA"
  },
  GENE: {
    prefix: 'gene',
    decription: "gene expressions"
  },
  GENE_GROUP: {
    prefix: 'gene_group',
    decription: "multiple gene expressions"
  }
}


export const PREFIX_TO_ASSET_TYPE = {
  bg: 'BACKGROUND',
  fic: 'FICTURE',
  hexa: 'HEXAGON',
  gene: 'GENE',
  gene_group: 'GENE_GROUP'
}

export const loadLayerAndDispatch = async (map, url, type, dispatch, zoomTo, catalogId, layerIdFromBookmark=null) => {

  const prefix = ASSET_TYPES[type]['prefix'];
  console.log("dispatching add layer event", map, url, type, zoomTo);
  switch (type) {
    case "FICTURE":
      dispatch({
        type: 'ADD_LAYER',
        payload: await addGeoTiffLayer(
          map, { url: url },
          prefix, null, null, zoomTo, catalogId, layerIdFromBookmark)
      });
      break;
    case "HEXAGON":
      dispatch({
        type: 'ADD_LAYER',
        payload: await addPMTilesCircleLayer(
          map, url, defaultFactorHexagonLayerOptions,
          prefix, zoomTo, catalogId, layerIdFromBookmark)
      });
      break;
    case "GENE":
      dispatch({
        type: 'ADD_LAYER',
        payload: await addPMTilesCircleLayer(
          map, url, defaultSgeLayerOptions,
          prefix, zoomTo, catalogId, layerIdFromBookmark)
      });
      break;
    case "GENE_GROUP":
      dispatch({
        type: 'ADD_LAYER',
        payload: await addPMTilesCircleGroupLayer(
          map, url, defaultSgeLayerOptions,
          prefix, zoomTo, catalogId, layerIdFromBookmark)
      });
      break;
    case "BACKGROUND":
      dispatch({
        type: 'ADD_LAYER',
        payload: await addPMTilesRasterLayer(
          map, url,
          prefix, zoomTo, catalogId, layerIdFromBookmark)
      });
      break;
  }
};

function AddAssetButton() {

  const { map } = useContext(MapContext);
  const { layers, dispatch } = useContext(LayerContext);
  const { catalog, setCatalog } = useCatalog();

  const [openDialog, setOpenDialog] = useState(false);
  const [url, setUrl] = useState('');
  const [type, setType] = useState('');
  const [catalogs, setCatalogs] = useState([]);
  const [newCatalogUrl, setNewCatalogUrl] = useState('');

  useEffect(() => {
    if (catalog?.id) {
      // Check if catalogs array already contains a catalog with the same id
      const existingIndex = catalogs.findIndex(c => c.id === catalog.id);

      // If the catalog exists, replace it. Otherwise, add the new catalog.
      if (existingIndex > -1) {
        const updatedCatalogs = [...catalogs];
        updatedCatalogs[existingIndex] = catalog;
        setCatalogs(updatedCatalogs);
      } else {
        setCatalogs([...catalogs, catalog]);
      }
    }
  }, [catalog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  const handleAddNewCatalog = async () => {
    // Add a new catalog to the catalogs array (you might want to fetch catalog data based on the URL here)
    const newCatalog = await loadCatalog(newCatalogUrl);
    setCatalogs([...catalogs, newCatalog]);
    setNewCatalogUrl(''); // Reset the input field after adding
    setCatalog(newCatalog);
  };
  const handleAddAsset = () => {
    // Call the AddAsset function here
    const zoomTo = layers.length === 0 ? true : false;
    loadLayerAndDispatch(map.current, url, type, dispatch, zoomTo, catalog?.id);
    handleCloseDialog();
  };
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const newCatalog = await loadCatalogAsIs(text); // Assume parseCatalog is a function you have to create
        setCatalogs([...catalogs, newCatalog]);
        setCatalog(newCatalog);
      };
      reader.readAsText(file);
    }
  };

  return (
    <>
      <Button className="addAssetButton"
        onClick={() => setOpenDialog(true)}
        startIcon={<AddIcon />}>
        Add Layer
      </Button>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md" // set maximum width to "md", "sm", "lg", or "xl"
        fullWidth={true} // makes the dialog take up the full width up to its maximum width
      >
        <DialogTitle>Add a layer</DialogTitle>
        <DialogContent>
          {catalogs.slice().reverse().map((cat, index) => (   // reversing the order
            <LayersFromCatalog
              key={index}
              catalog={cat}
              loadLayerAndDispatch={loadLayerAndDispatch}
              map={map}
              layers={layers}
              dispatch={dispatch}
              handleCloseDialog={handleCloseDialog}
              setCatalogs={setCatalogs}
              activeCatalog={catalog}
              setActiveCatalog={setCatalog}
            />
          ))}
          <hr />
          <Accordion key='additional' className="assetAccordion">
            <AccordionSummary className="assetAccordionSummary" expandIcon={<ExpandMoreIcon />}>
              External Asset
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <TextField
                autoFocus
                margin="dense"
                id="url"
                label="Asset URL"
                type="url"
                fullWidth
                value={url}
                onChange={e => setUrl(e.target.value)}
              />
              <Select value={type} onChange={e => setType(e.target.value)} fullWidth>
                {Object.entries(ASSET_TYPES).map(([key, asset]) => (
                  <MenuItem key={`item-${key}`} value={key}>
                    {asset.decription}
                  </MenuItem>
                ))}
              </Select>
              <DialogActions>
                <Button onClick={handleAddAsset}
                  variant="outlined" color="success"
                > Add </Button>
              </DialogActions>
            </AccordionDetails>
          </Accordion>
          <Accordion key='newCatalog' className="assetAccordion">
            <AccordionSummary className="assetAccordionSummary" expandIcon={<ExpandMoreIcon />}>
              Add Catalog from URL
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <TextField
                autoFocus
                margin="dense"
                label="Enter Catalog URL"
                type="url"
                fullWidth
                value={newCatalogUrl}
                onChange={e => setNewCatalogUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddNewCatalog()}
              />
              <Button onClick={handleAddNewCatalog} color="primary">
                Add Catalog
              </Button>
            </AccordionDetails>
          </Accordion>
          <Accordion key='newCatalogLocal' className="assetAccordion">
            <AccordionSummary className="assetAccordionSummary" expandIcon={<ExpandMoreIcon />}>
              Add Catalog from Local 
            </AccordionSummary>
            <AccordionDetails className="accordion-details">
              <Input
                type="file"
                inputProps={{ accept: '.yaml' }}
                onChange={handleFileChange}
              />
            </AccordionDetails>
          </Accordion>


          <DialogActions>
            <Button onClick={handleCloseDialog} variant="outlined" color="secondary">
              close
            </Button>
          </DialogActions>

        </DialogContent>
      </Dialog>
    </>
  );
}


export default AddAssetButton;

